//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  ImageFile,
  useUploadProductImagesMutation,
} from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { ProductDetailsPanel } from '@campaigns/components/shared/product-details-modal/ProductDetailsPanel';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { doAllVariantImagesMatch } from '@campaigns/utils/sampling/SamplingImageUtils';
import { getInitialProduct } from '@campaigns/utils/sampling/SamplingProductUtils';
import { isDefined } from '@utils/filterPredicates';

export enum ProductModalMode {
  View = 'view',
  Edit = 'edit',
  Create = 'create',
}

interface ProductModalState {
  product: EditableBVSamplingProduct | undefined;
  mode: ProductModalMode;
  action: ProductModalMode;
  originalProductId?: string;
}

interface ProductModalContextProps {
  addProductToCampaign?: (
    product: BVSamplingProduct,
    isEdit: boolean,
    originalProductId?: string,
  ) => void;
  clearAllVariantImages: () => void;
  closeProductDetailsModal: () => void;
  handleMainImageSelect: (file: File | undefined) => void;
  handleVariantImageSelect: (index: number, file: File | undefined) => void;
  removeVariantImage: (index: number) => void;
  isImageForAllVariants: boolean;
  isProductDetailsModalOpen: boolean;
  mainProductImage?: ImageFile;
  openProductDetailsModal: (
    product: BVSamplingProduct | undefined,
    mode: ProductModalMode,
  ) => void;
  productModalState: ProductModalState;
  setIsImageForAllVariants: (isImageForAllVariants: boolean) => void;
  setModalMode: (mode: ProductModalMode) => void;
  setShowImagePreview: (showImagePreview: boolean) => void;
  showImagePreview: boolean;
  updateProductInModalState: (productId: string) => void;
  uploadImageFilesFromProduct: (
    product: EditableBVSamplingProduct,
  ) => Promise<EditableBVSamplingProduct>;
  variantImages: Record<number, ImageFile | undefined>;
}

interface ProductModalProviderProps {
  children: ReactNode;
  addProductToCampaign?: (
    product: BVSamplingProduct,
    isEdit: boolean,
    originalProductId?: string,
  ) => void;
}

const ProductModalContext = createContext<ProductModalContextProps | undefined>(
  undefined,
);

export const ProductModalProvider = ({
  addProductToCampaign,
  children,
}: ProductModalProviderProps) => {
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] =
    useState(false);
  const [isImageForAllVariants, setIsImageForAllVariants] = useState(false);
  const [mainProductImage, setMainProductImage] = useState<ImageFile>();
  const [variantImages, setVariantImages] = useState<
    Record<number, ImageFile | undefined>
  >({});
  const [productModalState, setProductModalState] = useState<ProductModalState>(
    {
      product: undefined,
      mode: ProductModalMode.Create,
      action: ProductModalMode.Create,
      originalProductId: undefined,
    },
  );

  const [showImagePreview, setShowImagePreview] = useState(false);

  const { mutateAsync: uploadImagesAsync } = useUploadProductImagesMutation();

  const handleMainImageSelect = useCallback(
    (file: File | undefined) => {
      if (mainProductImage?.previewUrl) {
        URL.revokeObjectURL(mainProductImage.previewUrl);
      }

      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setMainProductImage({ file, previewUrl });
      } else {
        setMainProductImage(undefined);
      }
    },
    [mainProductImage],
  );

  const handleVariantImageSelect = useCallback(
    (index: number, file: File | undefined) => {
      setVariantImages((prev) => {
        // Cleanup previous URL if it exists
        if (prev[index]?.previewUrl) {
          URL.revokeObjectURL(prev[index].previewUrl);
        }

        // If no file, remove the entry for this index
        if (!file) {
          const { [index]: _, ...rest } = prev;
          return rest;
        }

        // Create new URL and update state immutably
        const previewUrl = URL.createObjectURL(file);
        setIsImageForAllVariants(false);

        return {
          ...prev,
          [index]: { file, previewUrl },
        };
      });
    },
    [],
  );

  const removeVariantImage = useCallback((index: number) => {
    setVariantImages((prev) => {
      const removedImage = prev[index];
      if (removedImage?.previewUrl) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      return Object.entries(prev).reduce<Record<number, ImageFile | undefined>>(
        (next, [entryIndex, image]) => {
          const numericIndex = Number(entryIndex);
          if (numericIndex > index) {
            next[numericIndex - 1] = image;
          } else if (numericIndex < index) {
            next[numericIndex] = image;
          }
          return next;
        },
        {},
      );
    });
  }, []);

  const uploadImageFilesFromProduct = useCallback(
    (product: EditableBVSamplingProduct) => {
      return uploadImagesAsync({
        product,
        mainImage: mainProductImage,
        variantImages,
        isImageForAllVariants,
      });
    },
    [uploadImagesAsync, mainProductImage, variantImages, isImageForAllVariants],
  );

  // Revokes preview URLs and clears any pending per-variant uploads, e.g. when the
  // parent image is applied to all variants and per-variant uploads become stale.
  const clearAllVariantImages = useCallback(() => {
    setVariantImages((prev) => {
      Object.values(prev).forEach((variantImage) => {
        if (variantImage?.previewUrl) {
          URL.revokeObjectURL(variantImage.previewUrl);
        }
      });
      return {};
    });
  }, []);

  const openProductDetailsModal = useCallback(
    (
      product: BVSamplingProduct | undefined,
      mode: ProductModalMode = ProductModalMode.Create,
    ) => {
      const initialProduct = getInitialProduct(product);
      setProductModalState({
        product: initialProduct,
        originalProductId: product?.targetCatalogProductId,
        mode,
        action: mode,
      });
      setIsProductDetailsModalOpen(true);
      setShowImagePreview(isDefined(initialProduct.imgUrl));

      // Check if existing product has matching variant images
      const isAllVariantsMatch = doAllVariantImagesMatch(
        initialProduct.imgUrl,
        initialProduct.variations,
      );
      setIsImageForAllVariants(isAllVariantsMatch);
    },
    [],
  );

  const closeProductDetailsModal = useCallback(() => {
    setIsProductDetailsModalOpen(false);
    // Clean up image state when modal closes
    if (mainProductImage?.previewUrl) {
      URL.revokeObjectURL(mainProductImage.previewUrl);
    }
    Object.values(variantImages).forEach((variantImage) => {
      if (variantImage?.previewUrl) {
        URL.revokeObjectURL(variantImage.previewUrl);
      }
    });
    setMainProductImage(undefined);
    setVariantImages({});
  }, [mainProductImage, variantImages]);

  const updateProductInModalState = useCallback((productId: string) => {
    setProductModalState((prevState) => {
      const updatedProduct = prevState.product
        ? { ...prevState.product, targetCatalogProductId: productId }
        : undefined;

      return {
        ...prevState,
        product: updatedProduct,
      };
    });
  }, []);

  const setModalMode = useCallback((mode: ProductModalMode) => {
    setProductModalState((prevState) => ({
      ...prevState,
      mode,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      clearAllVariantImages,
      closeProductDetailsModal,
      handleMainImageSelect,
      handleVariantImageSelect,
      removeVariantImage,
      isImageForAllVariants,
      isProductDetailsModalOpen,
      mainProductImage,
      openProductDetailsModal,
      productModalState,
      setIsImageForAllVariants,
      updateProductInModalState,
      addProductToCampaign,
      uploadImageFilesFromProduct,
      variantImages,
      showImagePreview,
      setShowImagePreview,
      setModalMode,
    }),
    [
      isProductDetailsModalOpen,
      productModalState,
      openProductDetailsModal,
      closeProductDetailsModal,
      addProductToCampaign,
      mainProductImage,
      variantImages,
      handleMainImageSelect,
      handleVariantImageSelect,
      removeVariantImage,
      clearAllVariantImages,
      uploadImageFilesFromProduct,
      isImageForAllVariants,
      setIsImageForAllVariants,
      showImagePreview,
      setShowImagePreview,
      updateProductInModalState,
      setModalMode,
    ],
  );

  return (
    <ProductModalContext.Provider value={contextValue}>
      {children}
      {isProductDetailsModalOpen && <ProductDetailsPanel />}
    </ProductModalContext.Provider>
  );
};

export const useProductModal = () => {
  const context = useContext(ProductModalContext);
  if (context === undefined) {
    throw new Error(
      'useProductModal must be used within a ProductModalProvider',
    );
  }
  return context;
};
